import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../api';

const THEME = {
    primary: '#C0722C',
    secondary: '#2C1A10',
    background: '#FDF8F3',
    text: '#2C1A10',
    accent: '#EAD7BB'
};

const ICONS = {
    speaker: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>,
    minister: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><line x1="6" y1="11" x2="6" y2="11"></line><line x1="10" y1="11" x2="10" y2="11"></line><line x1="14" y1="11" x2="14" y2="11"></line></svg>,
    user: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
};

const RoleManagement = () => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/users?status=active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(data);
        } catch (err) { console.error(err); }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleAssignPrivilege = async (user, privilege) => {
        if (user.designation === 'SuperAdmin') return;

        const hasPrivilege = user.privileges.includes(privilege);
        const action = hasPrivilege ? 'Revoke' : 'Grant';

        // Dual Confirmation for High Security Roles
        if (!hasPrivilege) {
            const result = await Swal.fire({
                title: '<span style="font-family: \'Outfit\', sans-serif; font-weight: 800; color: #2C1A10;">Confirm Authority Grant</span>',
                html: `<p style="font-family: 'Outfit', sans-serif; color: #666;">Are you sure you want to appoint <strong>${user.firstName} ${user.lastName}</strong> as ${privilege}? This will grant them high-level control.</p>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#C0722C',
                confirmButtonText: 'Yes, I Confirm',
                cancelButtonText: 'Cancel',
                background: '#FFFFFF',
                borderRadius: '24px'
            });
            if (!result.isConfirmed) return;
        }

        // Action
        try {
            const token = localStorage.getItem('token');
            // Toggle privilege logic
            let newPrivileges = hasPrivilege
                ? user.privileges.filter(p => p !== privilege)
                : [...user.privileges, privilege];

            await axios.post(`${API_BASE_URL}/api/users/${user._id}/privileges`,
                { privileges: newPrivileges },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            Swal.fire({
                title: 'Dossier Fragment Updated',
                text: `Privilege ${action}d. User session has been reset for security compliance.`,
                icon: 'success',
                confirmButtonColor: THEME.primary,
                background: '#FFFFFF',
                borderRadius: '24px'
            });
            fetchUsers();
        } catch (err) {
            Swal.fire('Error', 'Update failed', 'error');
        }
    };

    const handleResetPrivileges = async (user) => {
        if (user.designation === 'SuperAdmin') return;

        const result = await Swal.fire({
            title: 'Confirm Reset',
            text: `Revert all special privileges for ${user.firstName} ${user.lastName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#D32F2F',
            confirmButtonText: 'Yes, Reset',
            background: '#FFFFFF',
            borderRadius: '24px'
        });

        if (!result.isConfirmed) return;

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/users/${user._id}/privileges`,
                { privileges: [] }, // Send empty array to reset
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire('Reset Complete', 'User privileges restored to default duties.', 'success');
            fetchUsers();
        } catch (err) {
            Swal.fire('Error', 'Reset failed', 'error');
        }
    };

    const handleTempRole = async (user, role) => {
        try {
            const token = localStorage.getItem('token');
            const result = await Swal.fire({
                title: `Appoint Temporary ${role}`,
                text: `This assignment will be active for 24 hours. The user's existing session will be reset. Proceed?`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: THEME.primary,
                background: '#FFFFFF',
                borderRadius: '24px'
            });

            if (!result.isConfirmed) return;

            await axios.post(`${API_BASE_URL}/api/admin/temp-role`,
                { userId: user._id, role },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            Swal.fire('Success', `Temporary ${role} role assigned.`, 'success');
            fetchUsers();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Assignment failed', 'error');
        }
    };

    const handleRevokeTempRole = async (user) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/admin/temp-role/revoke`,
                { userId: user._id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire('Revoked', 'Temporary role reset.', 'success');
            fetchUsers();
        } catch (err) {
            Swal.fire('Error', 'Revocation failed', 'error');
        }
    };

    // Filter Logic
    const filteredUsers = users.filter(user => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            user.firstName.toLowerCase().includes(term) ||
            user.lastName.toLowerCase().includes(term) ||
            user.email.toLowerCase().includes(term);

        const matchesRole = roleFilter ? user.designation === roleFilter : true;

        return matchesSearch && matchesRole;
    });

    const uniqueDesignations = [...new Set(users.map(u => u.designation))].filter(d => d !== 'SuperAdmin');

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <style>
                {`
                .custom-input {
                    width: 100%;
                    padding: 16px 20px;
                    border-radius: 12px;
                    border: 1.5px solid #E5E5E5;
                    font-size: 16px;
                    box-sizing: border-box;
                    outline: none;
                    transition: all 0.3s ease;
                    background-color: #FAFAFA;
                    font-family: inherit;
                    color: #333;
                    -webkit-appearance: none;
                    appearance: none;
                }
                .custom-input:focus {
                    border-color: #C0722C;
                    box-shadow: 0 0 0 4px rgba(192, 114, 44, 0.1);
                    background-color: #fff;
                }
                `}
            </style>
            <div style={{ marginBottom: '40px' }}>
                <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800', color: '#2C1A10' }}>Privilege Governance</h1>
                <p style={{ margin: 0, color: '#999', fontSize: '16px' }}>
                    Critical assignment of functional powers. Changes force immediate session invalidation for target entities.
                </p>
            </div>

            {/* Filter Bar */}
            <div style={{ position: 'relative', marginBottom: '45px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <div style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#C0722C',
                        display: 'flex',
                        zIndex: 2
                    }}>
                        {ICONS.search}
                    </div>
                    <input
                        placeholder="Search by Identity, Secretariat Email or Personnel Code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="custom-input"
                        style={{ paddingLeft: '48px' }}
                    />
                </div>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1.5px solid #E5E5E5',
                        fontSize: '14px',
                        outline: 'none',
                        fontWeight: '600',
                        backgroundColor: '#FAFAFA',
                        boxShadow: 'none',
                        minWidth: '200px',
                        height: '54px'
                    }}
                >
                    <option value="">All Designations</option>
                    {uniqueDesignations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '25px' }}>
                {filteredUsers.map(user => {
                    const isSystemAdmin = user.designation === 'SuperAdmin';
                    return (
                        <div key={user._id} style={{
                            backgroundColor: isSystemAdmin ? '#FAFAFA' : 'white',
                            padding: '30px',
                            borderRadius: '24px',
                            boxShadow: '0 15px 45px rgba(0,0,0,0.04)',
                            border: '1px solid #F0E8DB',
                            borderTop: user.privileges.includes('Speaker') ? `6px solid ${THEME.primary}` : '1px solid #F0E8DB',
                            transition: 'all 0.3s ease',
                            opacity: isSystemAdmin ? 0.8 : 1
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                                <div>
                                    <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800', color: '#2C1A10' }}>{user.firstName} {user.lastName}</h3>
                                    <div style={{ fontSize: '12px', color: '#999', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                                        <span style={{ color: THEME.primary }}>{ICONS.user}</span>
                                        {user.email}
                                    </div>
                                </div>
                                <span style={{
                                    padding: '6px 12px',
                                    backgroundColor: isSystemAdmin ? '#2C1A10' : '#FDF8F3',
                                    color: isSystemAdmin ? '#FFF' : THEME.primary,
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '800',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                    border: '1px solid #F0E8DB'
                                }}>
                                    {user.designation}
                                </span>
                            </div>

                            {/* Temporary Roles (24h) */}
                            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#FDF8F3', borderRadius: '14px', border: '1px solid #EAD7BB' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase' }}>Temporary Authority</span>
                                    {user.tempRole && new Date(user.tempRoleExpiresAt) > new Date() && (
                                        <span style={{ padding: '4px 8px', backgroundColor: '#C0722C', color: 'white', borderRadius: '4px', fontSize: '10px', fontWeight: '800' }}>
                                            ACTIVE: {user.tempRole}
                                        </span>
                                    )}
                                </div>

                                {user.tempRole && new Date(user.tempRoleExpiresAt) > new Date() ? (
                                    <button
                                        onClick={() => handleRevokeTempRole(user)}
                                        disabled={isSystemAdmin}
                                        style={{
                                            width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                                            backgroundColor: isSystemAdmin ? '#E5E5E5' : '#2C1A10',
                                            color: isSystemAdmin ? '#CCC' : 'white',
                                            fontWeight: '700',
                                            cursor: isSystemAdmin ? 'not-allowed' : 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Revoke Temporary Role
                                    </button>
                                ) : (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => handleTempRole(user, 'Speaker')}
                                            disabled={isSystemAdmin || user.designation === 'Speaker' || user.privileges.includes('Speaker')}
                                            style={{
                                                flex: 1, padding: '10px', borderRadius: '8px',
                                                border: `1.5px solid ${isSystemAdmin || user.designation === 'Speaker' || user.privileges.includes('Speaker') ? '#E5E5E5' : '#C0722C'}`,
                                                backgroundColor: 'transparent',
                                                color: isSystemAdmin || user.designation === 'Speaker' || user.privileges.includes('Speaker') ? '#CCC' : '#C0722C',
                                                fontWeight: '700',
                                                cursor: isSystemAdmin || user.designation === 'Speaker' || user.privileges.includes('Speaker') ? 'not-allowed' : 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Switch to Speaker (Temp)
                                        </button>
                                        <button
                                            onClick={() => handleTempRole(user, 'Minister')}
                                            disabled={isSystemAdmin || user.designation === 'Minister' || user.privileges.includes('Minister')}
                                            style={{
                                                flex: 1, padding: '10px', borderRadius: '8px',
                                                border: `1.5px solid ${isSystemAdmin || user.designation === 'Minister' || user.privileges.includes('Minister') ? '#E5E5E5' : '#2C1A10'}`,
                                                backgroundColor: 'transparent',
                                                color: isSystemAdmin || user.designation === 'Minister' || user.privileges.includes('Minister') ? '#CCC' : '#2C1A10',
                                                fontWeight: '700',
                                                cursor: isSystemAdmin || user.designation === 'Minister' || user.privileges.includes('Minister') ? 'not-allowed' : 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Switch to Minister (Temp)
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Reset Option */}
                            {(user.privileges.length > 0 || (user.tempRole && new Date(user.tempRoleExpiresAt) > new Date())) && !isSystemAdmin && (
                                <button
                                    onClick={async () => {
                                        if (user.tempRole) await handleRevokeTempRole(user);
                                        await handleResetPrivileges(user);
                                    }}
                                    style={{
                                        width: '100%',
                                        marginTop: '15px',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        border: '1px dashed #D32F2F',
                                        backgroundColor: '#FFF5F5',
                                        color: '#D32F2F',
                                        cursor: 'pointer',
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        transition: 'all 0.2s',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"></polyline><polyline points="23 20 23 14 17 14"></polyline><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path></svg>
                                    Reset Permissions
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RoleManagement;
